import React, { useMemo } from "react";
import { styled, Link, Stack, Typography, Divider, Box, SxProps } from "@mui/material";
import OrderRecapAccordion from "./OrderRecapAccordion/order-recap-accordion";
import { thumbnailService } from "../../../Core/Api/services";
import { BasketTopLevelItem } from "../../../Pages/Basket/basket";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { OpenLinkInNewTab } from "../../../Utils/helpers";
import { routePaths } from "../../../Router/router";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { Img } from "react-image";

export type OrderRecapCardProps = {
  className?: string;
  sx?: SxProps;

  group: BasketTopLevelItem;
  customers: DbCustomerInfos[];
};

const OrderRecapCard: React.FC<OrderRecapCardProps> = (props) => {
  const { group, customers, ...otherProps } = props;

  const [groupPrice, groupCurrency] = useMemo(() => {
    let sum = 0;
    let currency: CurrencyTypes = CurrencyTypes.EUR;

    group.items.forEach((layered) => {
      layered.items.forEach((item) => {
        if (item.overridenPrice?.price) {
          sum += item.overridenPrice?.price;
          if (item.overridenPrice?.currency) currency = item.overridenPrice?.currency;
        } else if (item.totalPrice?.price) {
          sum += item.totalPrice?.price;
          if (item.totalPrice?.currency) currency = item.totalPrice?.currency;
        }
      });
    });

    return [sum, currency];
  }, [group]);

  const imageUrl = React.useMemo(() => {
    return thumbnailService.GetObjectPosterUrl(group.id, ThumbnailSize.small);
  }, [group]);

  return (
    <Wrapper spacing={2.5} direction="row" {...otherProps}>
      <Img src={imageUrl} alt="Poster" />
      <Stack flex={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mr={2}>
          <Stack>
            <Typography color="text.primary" variant="subtitle1" fontWeight={600}>
              {group.name}
            </Typography>
            <Link
              component="button"
              color="primary.text"
              underline="hover"
              variant="body2"
              onClick={() => OpenLinkInNewTab(routePaths.title.path + "/" + group.id)}
            >
              #{group.id}
            </Link>
          </Stack>
          <Typography variant="subtitle1" fontWeight={600}>
            {groupPrice} {groupCurrency === CurrencyTypes.EUR ? "€" : "$"}
          </Typography>
        </Stack>
        <Divider sx={{ mt: 2, mb: 2 }} />
        <Stack spacing={2} mr={2}>
          {group.items.map((layered) => (
            <Box key={`layeredgroup-${group.id}-${layered.layer}`}>
              <OrderRecapAccordion layeredItems={layered} customers={customers} />
            </Box>
          ))}
        </Stack>
      </Stack>
    </Wrapper>
  );
};

const Wrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
  borderRadius: 6,
  boxSizing: "border-box",
  overflow: "hidden",
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: theme.palette.mode === "dark" ? "transparent" : theme.palette.background.paper,
  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
  },
  figure: {
    width: 80,
    height: 106,
    borderRadius: 2,
    overflow: "hidden",
  },
}));

export default OrderRecapCard;
